import { Component, Input, ViewChild, TemplateRef, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { EsignPositonComponent } from 'src/app/utilities/esign-positon/esign-positon.component';
import { RegExpPatterns } from 'src/app/share/enums/regex-pattern.enum';
import { PublicEsignService } from '../../../share/services/public-esign/publicesign.service'
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
    selector: 'app-public-esign',
    templateUrl: './public-esign.component.html',
    styleUrls: ['./public-esign.component.scss'],
    standalone: false
})
export class PublicEsignComponent {
  @Input() isFileUploading: boolean = false;
  @ViewChild('file') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('paymentModal') paymentModalTpl!: TemplateRef<any>;

  userForm: FormGroup;
  allowedFileType: string = '.pdf';
  verifyLoader: boolean = false;
  uploadErrorMessage: string = '';
  uploadedFileName: string = '';
  selectedFile: any;
  paymentUrl: SafeResourceUrl | null = null;
  paymentModalRef?: NgbModalRef;

  constructor(private readonly fb: FormBuilder,
    private readonly modalService: NgbModal,
    private readonly PublicEsignService: PublicEsignService,
    private readonly sanitizer: DomSanitizer) 
  {
    this.userForm = this.fb.group({
      name: ['', Validators.required],
      phoneNumber: ['', [Validators.required, Validators.pattern(RegExpPatterns.MobilePattern)]]
    });
  }

  triggerFileInputClick() {
    this.fileInput.nativeElement.click();
  }

  upload(event: any) {
    this.isFileUploading = true;
    const maxFileSizeBytes = 10 * 1024 * 1024;
    const file = event?.target?.files?.[0];
    if (file) {
      const fileExtension = this.getFileExtension(file.name);
      if (fileExtension !== this.allowedFileType) {
        this.uploadErrorMessage = 'Only PDF files are allowed.';
        this.resetFileInput();
        this.isFileUploading = false;
        return;
      }
      if (file.size > maxFileSizeBytes) {
        this.uploadErrorMessage = 'File size exceeds the maximum limit of 10 MB.';
        this.resetFileInput();
        this.isFileUploading = false;
        return;
      }
      this.selectedFile = file;
      this.uploadedFileName = file.name;
      this.uploadErrorMessage = '';
    } else {
      this.uploadErrorMessage = '';
    }
    event.target.value = null;
    this.isFileUploading = false;
  }

  getFileExtension(fileName: string): string {
    return `.${fileName.split('.').pop()?.toLowerCase()}`;
  }

  removeFile() {
    this.selectedFile = null;
    this.uploadedFileName = '';
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
    this.uploadErrorMessage = '';
  }

  onSubmit() {
    if (this.selectedFile) {
      this.navigateToEsignPosition(this.selectedFile);
    } else {
      this.uploadErrorMessage = 'Please select a file before submitting.';
    }
  }

  navigateToEsignPosition(file: File) {
    const fileReader = new FileReader();
    fileReader.onload = () => {
      const pdfSrc = fileReader.result as string;
      const esignPositionPopup = this.modalService.open(EsignPositonComponent, {
        backdrop: 'static',
        keyboard: false,
        centered: true,
        fullscreen: true,
      });

      if (esignPositionPopup.componentInstance) {
        esignPositionPopup.componentInstance.pdfSrc = pdfSrc;
        esignPositionPopup.componentInstance.isShowUsers = false;

        esignPositionPopup.result.then((result) => {
          if (result) {
            this.generatePaymentLink(this.userForm.value)
          }
        }).catch((error) => {
          console.error('Modal dismissed with error:', error);
        });

      }
    };
    fileReader.onerror = () => {
      this.uploadErrorMessage = 'Error reading the file.';
    };
    fileReader.readAsDataURL(file);
  }

   resetFileInput() {
    this.fileInput.nativeElement.value = '';
    this.selectedFile = null;
    this.uploadedFileName = '';
  }

  generatePaymentLink(values: any) {
    this.verifyLoader = true;
    let formData = new FormData();
    formData.append('file', this.selectedFile, this.selectedFile.name);
    formData.append('name', values.name);
    formData.append('phoneNumber', values.phoneNumber);
    this.PublicEsignService.getPaymentLink(formData)?.subscribe({
      next: (res: any) => {
        this.verifyLoader = false;
        if (res['success']) {
          this.openPaymentIframe(res.data.short_url);
        } else {
          this.uploadErrorMessage = 'Failed to generate payment link.';
        }
      },
      error: (err) => {
        this.verifyLoader = false;
        this.uploadErrorMessage = 'Failed to generate payment link.';
      },
    });
  }

  openPaymentIframe(url: string) {
    this.paymentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.paymentModalRef = this.modalService.open(this.paymentModalTpl, {
      backdrop: 'static',
      size: 'xl',
      centered: true,
      scrollable: true
    });
  }

  closePaymentModal() {
    if (this.paymentModalRef) {
      this.paymentModalRef.close();
      this.paymentModalRef = undefined;
    }
    this.paymentUrl = null;
  }
}
